<template>
  <div>
    <v-container class="py-0">
      <transition name="fade" mode="out-in" appear>
        <PageTitle class="d-block d-lg-none">Contractors</PageTitle>
      </transition>

      <transition name="slide-left-fade" mode="out-in" appear>
        <v-tabs
          v-model="tab"
          class="mt-5 mt-sm-1"
          color="tabColor"
          slider-color="tabColor"
          center-active
          background-color="tertiary"
          show-arrows
          height="55"
        >
          <v-tab ripple>
            All
          </v-tab>
          <v-tab ripple>
            No bank details
          </v-tab>
          <v-tab ripple>
            <span class="d-md-none">No email</span> 
            <span class="d-none d-md-block">No email address</span> 
          </v-tab>
          <v-tab ripple>
            Insufficient funds
          </v-tab>
          <v-spacer></v-spacer>
          <span class="d-none d-lg-block">
            <PageTitle>Contractors</PageTitle>
          </span>
        </v-tabs>
      </transition>

      <transition name="slide-fade" mode="out-in" appear>
        <div>
          <v-tabs-items v-model="tab" class="pt-2 tertiary">
            <v-tab-item>
              <Skeleton v-if="isLoadingAll && !allRecords.Data"/>
              <ContractorsRecordsTable
                :headers="headers"
                v-if="!isLoadingAll || allRecords.Data"
                :tableData="allRecords"
                :isLoading="isLoadingAll"
                msg="All"
                @paginate="handlePaginationUpdate"
              />   
            </v-tab-item>
            <v-tab-item>
              <Skeleton v-if="isLoadingBankless && !bankless.Data"/>
              <ContractorsRecordsTable
                :headers="headers"
                v-if="!isLoadingBankless || bankless.Data"
                :tableData="bankless"
                :isLoading="isLoadingBankless"
                msg="Without bank details"
                @paginate="handlePaginationUpdate"
              />   
            </v-tab-item>
            <v-tab-item>
              <Skeleton v-if="isLoadingEmailless && !emailless.Data"/>
              <ContractorsRecordsTable
                :headers="headers"
                v-if="!isLoadingEmailless || emailless.Data"
                :tableData="emailless"
                :isLoading="isLoadingEmailless"
                msg="Without email address"
                @paginate="handlePaginationUpdate"
              />  
            </v-tab-item>           
            <v-tab-item>
              <Skeleton v-if="isLoadingOnHold && !onHold.Data"/>
              <ContractorsRecordsTable
                :headers="headers"
                v-if="!isLoadingOnHold || onHold.Data"
                :tableData="onHold"
                :isLoading="isLoadingOnHold"
                msg="Insufficient funds"
                @paginate="handlePaginationUpdate"
              />  
            </v-tab-item>  
          </v-tabs-items>
        </div>
      </transition>

      <v-row>
        <v-col cols="12">
          <v-expansion-panels multiple class="mt-8">
            <v-expansion-panel>
              <v-expansion-panel-header ripple class="secondary lighten-3 white--text" >
                <div class="d-flex align-center justify-start">
                  <!-- <v-icon class="mr-3" color="white">mdi-account-tie</v-icon> -->
                  <v-icon class="mr-3" color="white">mdi-tools</v-icon>
                  Edit Contractor
                </div>
                <template v-slot:actions>
                  <v-icon color="white">$expand</v-icon>
                </template>
                </v-expansion-panel-header>
              <v-expansion-panel-content>
                <EditContractor />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Skeleton from '../components/SkeletonLoaders/SkeletonLoaderTable.vue'
import ContractorsRecordsTable from '../components/Contractors/ContractorsRecordsTable.vue'
import EditContractor from '@/components/Edits/EditContractor.vue'
import PageTitle from '@/components/Slots/PageTitle.vue'

export default {
  components: {
    Skeleton,
    ContractorsRecordsTable,
    EditContractor,
    PageTitle
  },
  data() {
    return {
      tab: null,
      headers: [
        {
          text: 'Name',
          align: 'left',
          value: 'ContractorName',
        },
        { text: 'Balance', value: 'ContractorBalance', sortable: true, sort: (a, b) => b - a  },
        { text: 'Account no.', value: 'AccountNumber' },
        { text: '', value: 'actions', align: 'end' },
        // { text: 'Application', value: 'Invite' },
        // { text: 'Support', value: 'Message' },
      ],
      pagination: {
        all: { page: 1, itemsPerPage: 10 },
        bankless: { page: 1, itemsPerPage: 10 },
        emailless: { page: 1, itemsPerPage: 10 },
        onHold: { page: 1, itemsPerPage: 10 }
      },
      loadedTabs: {
        all: false,
        bankless: false,
        emailless: false,
        onHold: false
      }
    }
  },
  created() {
    // Only load the first tab on initial load
    this.loadAllContractors()
    this.loadedTabs.all = true
  },
  watch: {
    tab(newTab) {
      // Load data for the selected tab if not already loaded
      const tabMap = {
        0: 'all',
        1: 'bankless',
        2: 'emailless',
        3: 'onHold'
      }
      
      const tabType = tabMap[newTab]
      if (tabType && !this.loadedTabs[tabType]) {
        this.loadTabData(tabType)
        this.loadedTabs[tabType] = true
      }
    }
  },
  computed: {
    ...mapState({
        allRecords: state => ({
          Data: state.ContractorsRecords.contractorsAll,
          Count: state.ContractorsRecords.totalCount.all
        }),
        bankless: state => ({
          Data: state.ContractorsRecords.bankless,
          Count: state.ContractorsRecords.totalCount.bankless
        }),
        emailless: state => ({
          Data: state.ContractorsRecords.emailless,
          Count: state.ContractorsRecords.totalCount.emailless
        }),
        onHold: state => ({
          Data: state.ContractorsRecords.onHold,
          Count: state.ContractorsRecords.totalCount.onHold
        }),
        loadingStates: state => state.ContractorsRecords.loading
    }),
    isLoadingAll() {
      return this.loadingStates.all
    },
    isLoadingBankless() {
      return this.loadingStates.bankless
    },
    isLoadingEmailless() {
      return this.loadingStates.emailless
    },
    isLoadingOnHold() {
      return this.loadingStates.onHold
    }
  },
  methods: {
    async loadTabData(tabType) {
      switch(tabType) {
        case 'all':
          await this.loadAllContractors()
          break
        case 'bankless':
          await this.loadBanklessContractors()
          break
        case 'emailless':
          await this.loadEmaillessContractors()
          break
        case 'onHold':
          await this.loadOnHoldContractors()
          break
      }
    },
    async loadAllContractors(page = 1, itemsPerPage = null, filters = {}) {
      if (itemsPerPage !== null) {
        this.pagination.all.itemsPerPage = itemsPerPage
      }
      const offset = (page - 1) * this.pagination.all.itemsPerPage
      await this.$store.dispatch('ContractorsRecords/getAllContractorsRecords', {
        offset,
        limit: this.pagination.all.itemsPerPage,
        forceRefresh: itemsPerPage !== null, // Force refresh when items per page changes
        ...filters
      })
      this.pagination.all.page = page
    },
    async loadBanklessContractors(page = 1, itemsPerPage = null, filters = {}) {
      if (itemsPerPage !== null) {
        this.pagination.bankless.itemsPerPage = itemsPerPage
      }
      const offset = (page - 1) * this.pagination.bankless.itemsPerPage
      await this.$store.dispatch('ContractorsRecords/getContractorsBanklessRecords', {
        offset,
        limit: this.pagination.bankless.itemsPerPage,
        forceRefresh: itemsPerPage !== null,
        ...filters
      })
      this.pagination.bankless.page = page
    },
    async loadEmaillessContractors(page = 1, itemsPerPage = null, filters = {}) {
      if (itemsPerPage !== null) {
        this.pagination.emailless.itemsPerPage = itemsPerPage
      }
      const offset = (page - 1) * this.pagination.emailless.itemsPerPage
      await this.$store.dispatch('ContractorsRecords/getContractorsEmaillessRecords', {
        offset,
        limit: this.pagination.emailless.itemsPerPage,
        forceRefresh: itemsPerPage !== null,
        ...filters
      })
      this.pagination.emailless.page = page
    },
    async loadOnHoldContractors(page = 1, itemsPerPage = null, filters = {}) {
      if (itemsPerPage !== null) {
        this.pagination.onHold.itemsPerPage = itemsPerPage
      }
      const offset = (page - 1) * this.pagination.onHold.itemsPerPage
      await this.$store.dispatch('ContractorsRecords/getContractorsOnHold', {
        offset,
        limit: this.pagination.onHold.itemsPerPage,
        forceRefresh: itemsPerPage !== null,
        ...filters
      })
      this.pagination.onHold.page = page
    },
    handlePaginationUpdate(type, page, itemsPerPage, filters = {}) {
      // Mark tab as loaded when user interacts with pagination
      if (!this.loadedTabs[type]) {
        this.loadedTabs[type] = true
      }
      
      // Update pagination state immediately for responsive UI
      this.pagination[type].page = page
      if (itemsPerPage) {
        this.pagination[type].itemsPerPage = itemsPerPage
      }
      
      switch(type) {
        case 'all':
          this.loadAllContractors(page, itemsPerPage, filters)
          break
        case 'bankless':
          this.loadBanklessContractors(page, itemsPerPage, filters)
          break
        case 'emailless':
          this.loadEmaillessContractors(page, itemsPerPage, filters)
          break
        case 'onHold':
          this.loadOnHoldContractors(page, itemsPerPage, filters)
          break
      }
    }
  }
}
</script>